<template>
  <div>
    <section class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">Manage Channel Viewing Slots</h1>

          <!-- Tabs for Slots vs Groups -->
          <b-tabs v-model="activeTab" type="is-boxed" class="has-margin-top-15">
            <!-- Individual Slots Tab -->
            <b-tab-item label="All Slots" icon="view-list">
              <div class="has-text-right has-margin-bottom-15">
                <b-button
                  type="is-primary"
                  icon-left="plus"
                  tag="router-link"
                  :to="{
                    name: 'CreateChannelViewingSlot',
                    params: { id: $route.params.id }
                  }"
                  >Add Slot</b-button
                >
              </div>
              <b-field>
                <b-input
                  v-model="search"
                  placeholder="Search slots..."
                  type="search"
                  icon="magnify"
                >
                </b-input>
              </b-field>
              <b-table
                :data="filteredChannelViewingSlots"
                :bordered="false"
                :striped="true"
                :narrowed="true"
                :hoverable="true"
                :loading="loading"
                :focusable="false"
                :mobile-cards="true"
                :paginated="true"
              >
                <template slot-scope="props">
                  <b-table-column field="name" label="Name">
                    <router-link
                      :to="{
                        name: 'EditChannelViewingSlot',
                        params: { id: props.row.id }
                      }"
                      >{{ props.row.name }}</router-link
                    >
                  </b-table-column>

                  <b-table-column field="slot_group_id" label="Group">
                    <b-tag v-if="props.row.slot_group_id" type="is-info">
                      {{ props.row.slot_group_id }}
                    </b-tag>
                    <span v-else class="has-text-grey">—</span>
                  </b-table-column>

                  <b-table-column field="sequence_order" label="Seq" width="60">
                    {{ props.row.sequence_order || 0 }}
                  </b-table-column>

                  <b-table-column field="days_after_previous" label="Days After" width="80">
                    {{ props.row.days_after_previous !== undefined ? props.row.days_after_previous : 1 }}
                  </b-table-column>

                  <b-table-column field="days" label="Days">
                    {{ formatDays(props.row.days) }}
                  </b-table-column>

                  <b-table-column field="start_time" label="Time">
                    {{ formatTime(props.row.start_time) }} - {{ formatTime(props.row.end_time) }}
                  </b-table-column>

                  <b-table-column class="has-text-centered" width="120">
                    <b-dropdown aria-role="list">
                      <button slot="trigger" class="button is-primary is-small">
                        <span>Actions</span>
                        <b-icon icon="menu-down"></b-icon>
                      </button>

                      <b-dropdown-item aria-role="listitem" :has-link="true"
                        ><router-link
                          :to="{
                            name: 'EditChannelViewingSlot',
                            params: { id: props.row.id }
                          }"
                          >Edit</router-link
                        ></b-dropdown-item
                      >
                      <b-dropdown-item
                        aria-role="listitem"
                        @click="confirmDelete(props.row)"
                        >Delete</b-dropdown-item
                      >
                    </b-dropdown>
                  </b-table-column>
                </template>

                <template slot="empty">
                  <section class="section">
                    <div class="content has-text-grey has-text-centered">
                      <p>
                        <b-icon icon="emoticon-sad" size="is-medium"> </b-icon>
                      </p>
                      <p>No viewing slots yet.</p>
                    </div>
                  </section>
                </template>
              </b-table>
            </b-tab-item>

            <!-- Slot Groups Tab -->
            <b-tab-item label="Slot Groups" icon="folder-multiple">
              <div class="has-text-right has-margin-bottom-15">
                <b-button
                  type="is-primary"
                  icon-left="plus"
                  @click="showCreateGroupModal = true"
                  >Create Group</b-button
                >
              </div>

              <!-- Groups List -->
              <div v-if="groups.length === 0" class="has-text-centered has-text-grey">
                <p class="has-margin-top-30 has-margin-bottom-30">
                  <b-icon icon="folder-open-outline" size="is-large"></b-icon>
                </p>
                <p>No slot groups yet. Create a group to organize your viewing slots.</p>
              </div>

              <div v-else class="groups-grid">
                <div
                  v-for="group in groups"
                  :key="group.id"
                  class="group-card box"
                >
                  <div class="group-header is-flex is-justify-content-space-between is-align-items-center">
                    <h3 class="title is-5 has-margin-bottom-0">{{ group.id }}</h3>
                    <div>
                      <b-button
                        size="is-small"
                        icon-left="pencil"
                        @click="editGroup(group)"
                      />
                      <b-button
                        size="is-small"
                        type="is-danger"
                        icon-left="delete"
                        @click="confirmDeleteGroup(group)"
                      />
                    </div>
                  </div>
                  <p class="has-text-grey has-margin-bottom-10">
                    {{ group.slots.length }} slot{{ group.slots.length !== 1 ? 's' : '' }}
                  </p>

                  <!-- Slots in this group -->
                  <div class="group-slots">
                    <div
                      v-for="slot in group.slots"
                      :key="slot.id"
                      class="slot-row is-flex is-justify-content-space-between is-align-items-center"
                    >
                      <div>
                        <b-tag
                          :type="slot.sequence_order === 0 ? 'is-success' : 'is-info'"
                          size="is-small"
                        >
                          {{ slot.sequence_order === 0 ? 'Prime' : `Repeat ${slot.sequence_order}` }}
                        </b-tag>
                        <span class="has-margin-left-10">{{ slot.name }}</span>
                      </div>
                      <div class="has-text-grey is-size-7">
                        {{ formatDays(slot.days) }} @ {{ formatTime(slot.start_time) }}
                        <span v-if="slot.days_after_previous > 0" class="has-text-info">
                          (+{{ slot.days_after_previous }}d)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="has-margin-top-10">
                    <b-button
                      size="is-small"
                      icon-left="plus"
                      @click="addSlotToGroup(group)"
                    >
                      Add Slot to Group
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Ungrouped Slots -->
              <div v-if="ungroupedSlots.length > 0" class="has-margin-top-30">
                <h3 class="title is-5">Ungrouped Slots</h3>
                <div class="ungrouped-list">
                  <div
                    v-for="slot in ungroupedSlots"
                    :key="slot.id"
                    class="ungrouped-slot is-flex is-justify-content-space-between is-align-items-center"
                  >
                    <div>
                      <strong>{{ slot.name }}</strong>
                      <span class="has-text-grey has-margin-left-10">
                        {{ formatDays(slot.days) }} @ {{ formatTime(slot.start_time) }}
                      </span>
                    </div>
                    <b-dropdown aria-role="list">
                      <button slot="trigger" class="button is-small">
                        <span>Add to Group</span>
                        <b-icon icon="menu-down"></b-icon>
                      </button>
                      <b-dropdown-item
                        v-for="group in groups"
                        :key="group.id"
                        @click="moveSlotToGroup(slot, group.id)"
                      >
                        {{ group.id }}
                      </b-dropdown-item>
                      <b-dropdown-item separator />
                      <b-dropdown-item @click="createGroupWithSlot(slot)">
                        + Create New Group
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
              </div>
            </b-tab-item>
          </b-tabs>

          <div class="has-text-centered has-margin-top-15">
            <div class="is-block has-margin-bottom-15">
              <b-button
                icon-left="arrow-left"
                class="is-medium has-margin-right-10"
                tag="router-link"
                :to="{
                  name: 'ManageChannels'
                }"
              >
                Back to Channels</b-button
              >
            </div>
          </div>
        </div>
      </CardComponent>
    </section>

    <!-- Create Group Modal -->
    <b-modal :active.sync="showCreateGroupModal" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Create Slot Group</p>
        </header>
        <section class="modal-card-body">
          <b-field label="Group ID">
            <b-input
              v-model="newGroupId"
              placeholder="e.g., weekday-prime"
            />
          </b-field>
          <p class="help">
            Use a descriptive ID like "weekday-prime", "weekend-morning", etc.
          </p>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showCreateGroupModal = false">Cancel</b-button>
          <b-button type="is-primary" @click="createGroup">Create</b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Edit Group Modal -->
    <b-modal :active.sync="showEditGroupModal" has-modal-card>
      <div class="modal-card" style="width: 700px">
        <header class="modal-card-head">
          <p class="modal-card-title">Edit Group: {{ editingGroup ? editingGroup.id : '' }}</p>
        </header>
        <section class="modal-card-body">
          <p class="has-margin-bottom-15">
            Drag slots to reorder sequence. Set "Days After Previous" for each repeat slot.
          </p>

          <div v-if="editingGroup" class="edit-group-slots">
            <div
              v-for="(slot, index) in editingGroup.slots"
              :key="slot.id"
              class="edit-slot-row box"
            >
              <div class="columns is-vcentered">
                <div class="column is-1">
                  <b-tag :type="index === 0 ? 'is-success' : 'is-info'">
                    {{ index }}
                  </b-tag>
                </div>
                <div class="column is-4">
                  <strong>{{ slot.name }}</strong>
                  <br />
                  <span class="has-text-grey is-size-7">
                    {{ formatDays(slot.days) }} @ {{ formatTime(slot.start_time) }}
                  </span>
                </div>
                <div class="column is-2">
                  <b-field label="Sequence" label-position="on-border">
                    <b-input
                      v-model.number="slot.sequence_order"
                      type="number"
                      min="0"
                      size="is-small"
                    />
                  </b-field>
                </div>
                <div class="column is-2">
                  <b-field label="Days After" label-position="on-border">
                    <b-input
                      v-model.number="slot.days_after_previous"
                      type="number"
                      min="0"
                      max="7"
                      size="is-small"
                      :disabled="slot.sequence_order === 0"
                    />
                  </b-field>
                </div>
                <div class="column is-3 has-text-right">
                  <b-button
                    size="is-small"
                    type="is-danger"
                    icon-left="close"
                    @click="removeSlotFromGroup(slot)"
                  >
                    Remove
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <div class="has-margin-top-15">
            <b-button icon-left="plus" size="is-small" @click="showAddToGroupDropdown = true">
              Add Existing Slot
            </b-button>
            <b-dropdown v-model="showAddToGroupDropdown" aria-role="list">
              <b-dropdown-item
                v-for="slot in ungroupedSlots"
                :key="slot.id"
                @click="addExistingSlotToEditingGroup(slot)"
              >
                {{ slot.name }}
              </b-dropdown-item>
              <b-dropdown-item v-if="ungroupedSlots.length === 0" disabled>
                No ungrouped slots available
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showEditGroupModal = false">Cancel</b-button>
          <b-button type="is-primary" :loading="saving" @click="saveGroupChanges">
            Save Changes
          </b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Add Slot to Group Modal -->
    <b-modal :active.sync="showAddSlotModal" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Add Slot to {{ targetGroup ? targetGroup.id : '' }}</p>
        </header>
        <section class="modal-card-body">
          <div v-if="ungroupedSlots.length === 0" class="notification is-warning">
            <p><strong>No ungrouped slots available</strong></p>
            <p>All slots are already assigned to groups. Create a new slot first, then add it to this group.</p>
            <b-button
              type="is-primary"
              size="is-small"
              class="has-margin-top-10"
              tag="router-link"
              :to="{
                name: 'CreateChannelViewingSlot',
                params: { id: $route.params.id },
                query: { groupId: targetGroup ? targetGroup.id : '' }
              }"
            >
              Create New Slot
            </b-button>
          </div>
          <b-field v-else label="Select Slot">
            <b-select v-model="selectedSlotToAdd" expanded>
              <option :value="null">Select a slot...</option>
              <option
                v-for="slot in ungroupedSlots"
                :key="slot.id"
                :value="slot"
              >
                {{ slot.name }} ({{ formatDays(slot.days) }} @ {{ formatTime(slot.start_time) }})
              </option>
            </b-select>
          </b-field>

          <div v-if="selectedSlotToAdd" class="has-margin-top-15">
            <b-field label="Sequence Order">
              <b-input
                v-model.number="selectedSlotToAdd.sequence_order"
                type="number"
                min="0"
              />
            </b-field>
            <p class="help">0 = Prime slot, 1+ = Repeat slots</p>

            <b-field label="Days After Previous" class="has-margin-top-10">
              <b-input
                v-model.number="selectedSlotToAdd.days_after_previous"
                type="number"
                min="0"
                max="7"
                :disabled="selectedSlotToAdd.sequence_order === 0"
              />
            </b-field>
            <p class="help">How many days after the previous slot in the sequence</p>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showAddSlotModal = false">Cancel</b-button>
          <b-button
            type="is-primary"
            :disabled="!selectedSlotToAdd"
            :loading="saving"
            @click="confirmAddSlotToGroup"
          >
            Add to Group
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CardComponent from '@/components/CardComponent'
import ChannelViewingSlotApi from '@/api/channelViewingSlots'
import format from 'date-fns/format'

export default {
  name: 'ManageChannelViewingSlots',
  components: {
    CardComponent
  },
  metaInfo: {
    title: 'Manage Channel Viewing Slots'
  },
  computed: {
    ...mapGetters(['user']),
    filteredChannelViewingSlots() {
      if (this.search !== '' && this.items.length > 0) {
        let query = this.search.toLowerCase()
        return this.items.filter(item =>
          item.name.toLowerCase().includes(query)
        )
      } else {
        return this.items
      }
    },
    groups() {
      const groupMap = {}
      this.items.forEach(slot => {
        if (slot.slot_group_id) {
          if (!groupMap[slot.slot_group_id]) {
            groupMap[slot.slot_group_id] = {
              id: slot.slot_group_id,
              slots: []
            }
          }
          groupMap[slot.slot_group_id].slots.push(slot)
        }
      })

      // Sort slots within each group by sequence_order
      Object.values(groupMap).forEach(group => {
        group.slots.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0))
      })

      return Object.values(groupMap).sort((a, b) => a.id.localeCompare(b.id))
    },
    ungroupedSlots() {
      return this.items.filter(slot => !slot.slot_group_id)
    }
  },
  created() {
    this.fetchChannelViewingSlots()
  },
  data() {
    return {
      loading: true,
      saving: false,
      items: [],
      search: '',
      item: {},
      activeTab: 0,
      // Create Group Modal
      showCreateGroupModal: false,
      newGroupId: '',
      // Edit Group Modal
      showEditGroupModal: false,
      editingGroup: null,
      showAddToGroupDropdown: false,
      // Add Slot to Group Modal
      showAddSlotModal: false,
      targetGroup: null,
      selectedSlotToAdd: null
    }
  },
  methods: {
    ...mapActions(['getUser']),
    formatDays(daysStr) {
      if (!daysStr) return ''
      const days = daysStr.split(',').map(Number)
      // Database stores: 1=Monday, 2=Tuesday, ..., 6=Saturday, 7=Sunday
      const dayNames = {
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
        7: 'Sun'
      }
      return days.map(d => dayNames[d] || '?').join(', ')
    },
    formatTime(timeValue) {
      if (!timeValue) return ''
      // Handle TIME strings (HH:mm:ss or HH:mm) from database
      if (typeof timeValue === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(timeValue)) {
        // Already a TIME string, just return HH:mm portion
        return timeValue.substring(0, 5)
      }
      // Handle Date objects or full datetime strings
      const date = new Date(timeValue)
      if (isNaN(date.getTime())) return ''
      return format(date, 'HH:mm')
    },
    confirmDelete(item) {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this slot?',
        onConfirm: async () => {
          this.loading = true
          await ChannelViewingSlotApi.remove(item)
          this.fetchChannelViewingSlots()
          this.$buefy.toast.open({
            message: 'Channel Viewing Slot deleted successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
        }
      })
    },
    async fetchChannelViewingSlots() {
      this.loading = true
      this.items = await ChannelViewingSlotApi.getByChannel(
        this.$route.params.id
      )
      this.loading = false
    },

    // Group Management Methods
    createGroup() {
      if (!this.newGroupId.trim()) {
        this.$buefy.toast.open({
          message: 'Please enter a group ID',
          position: 'is-top',
          type: 'is-warning'
        })
        return
      }

      // Check if group already exists
      if (this.groups.find(g => g.id === this.newGroupId.trim())) {
        this.$buefy.toast.open({
          message: 'A group with this ID already exists',
          position: 'is-top',
          type: 'is-warning'
        })
        return
      }

      this.$buefy.toast.open({
        message: `Group "${this.newGroupId}" created. Add slots to it using the "Add to Group" dropdown.`,
        position: 'is-top',
        type: 'is-success'
      })

      this.showCreateGroupModal = false
      this.newGroupId = ''
    },

    createGroupWithSlot(slot) {
      this.$buefy.dialog.prompt({
        message: 'Enter a name for the new group:',
        inputAttrs: {
          placeholder: 'e.g., weekday-prime',
          maxlength: 50
        },
        trapFocus: true,
        onConfirm: async value => {
          if (!value.trim()) return

          slot.slot_group_id = value.trim()
          slot.sequence_order = 0
          slot.days_after_previous = 0

          await this.updateSlot(slot)

          this.$buefy.toast.open({
            message: `Group "${value}" created with "${slot.name}" as prime slot`,
            position: 'is-top',
            type: 'is-success'
          })
        }
      })
    },

    editGroup(group) {
      // Deep copy to avoid modifying original data
      this.editingGroup = {
        id: group.id,
        slots: group.slots.map(s => ({ ...s }))
      }
      this.showEditGroupModal = true
    },

    confirmDeleteGroup(group) {
      this.$buefy.dialog.confirm({
        message: `Are you sure you want to delete group "${group.id}"? The slots will become ungrouped but won't be deleted.`,
        type: 'is-danger',
        onConfirm: async () => {
          this.saving = true

          // Remove group ID from all slots in this group
          for (const slot of group.slots) {
            slot.slot_group_id = null
            slot.sequence_order = 0
            slot.days_after_previous = 0
            await this.updateSlot(slot)
          }

          await this.fetchChannelViewingSlots()
          this.saving = false

          this.$buefy.toast.open({
            message: `Group "${group.id}" deleted. Slots are now ungrouped.`,
            position: 'is-top',
            type: 'is-success'
          })
        }
      })
    },

    async moveSlotToGroup(slot, groupId) {
      const nextSequence = this.getNextSequenceOrder(groupId)
      slot.slot_group_id = groupId
      slot.sequence_order = nextSequence
      slot.days_after_previous = nextSequence === 0 ? 0 : 1

      await this.updateSlot(slot)

      this.$buefy.toast.open({
        message: `"${slot.name}" added to group "${groupId}"`,
        position: 'is-top',
        type: 'is-success'
      })
    },

    getNextSequenceOrder(groupId) {
      const group = this.groups.find(g => g.id === groupId)
      if (!group || group.slots.length === 0) return 0
      const maxSeq = Math.max(...group.slots.map(s => s.sequence_order || 0))
      return maxSeq + 1
    },

    addSlotToGroup(group) {
      this.targetGroup = group
      this.selectedSlotToAdd = null
      this.showAddSlotModal = true
    },

    async confirmAddSlotToGroup() {
      if (!this.selectedSlotToAdd || !this.targetGroup) return

      this.saving = true

      this.selectedSlotToAdd.slot_group_id = this.targetGroup.id

      await this.updateSlot(this.selectedSlotToAdd)

      this.saving = false
      this.showAddSlotModal = false

      this.$buefy.toast.open({
        message: `"${this.selectedSlotToAdd.name}" added to group "${this.targetGroup.id}"`,
        position: 'is-top',
        type: 'is-success'
      })
    },

    addExistingSlotToEditingGroup(slot) {
      if (!this.editingGroup) return

      const nextSeq = this.editingGroup.slots.length
      this.editingGroup.slots.push({
        ...slot,
        slot_group_id: this.editingGroup.id,
        sequence_order: nextSeq,
        days_after_previous: nextSeq === 0 ? 0 : 1
      })
      this.showAddToGroupDropdown = false
    },

    removeSlotFromGroup(slot) {
      if (!this.editingGroup) return

      const index = this.editingGroup.slots.findIndex(s => s.id === slot.id)
      if (index >= 0) {
        // Mark for removal by setting slot_group_id to null
        this.editingGroup.slots[index]._removeFromGroup = true
        this.editingGroup.slots.splice(index, 1)
      }
    },

    async saveGroupChanges() {
      if (!this.editingGroup) return

      this.saving = true

      try {
        // Update all slots in the group
        for (const slot of this.editingGroup.slots) {
          await this.updateSlot(slot)
        }

        // Find slots that were removed from group (need to update them too)
        const originalGroup = this.groups.find(g => g.id === this.editingGroup.id)
        if (originalGroup) {
          for (const originalSlot of originalGroup.slots) {
            const stillInGroup = this.editingGroup.slots.find(s => s.id === originalSlot.id)
            if (!stillInGroup) {
              // Slot was removed from group
              originalSlot.slot_group_id = null
              originalSlot.sequence_order = 0
              originalSlot.days_after_previous = 0
              await this.updateSlot(originalSlot)
            }
          }
        }

        await this.fetchChannelViewingSlots()

        this.$buefy.toast.open({
          message: 'Group changes saved successfully!',
          position: 'is-top',
          type: 'is-success'
        })

        this.showEditGroupModal = false
      } catch (error) {
        console.error('Error saving group changes:', error)
        this.$buefy.toast.open({
          message: 'Error saving group changes',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.saving = false
      }
    },

    async updateSlot(slot) {
      // Prepare slot data for API
      const slotData = {
        id: slot.id,
        name: slot.name,
        start_time: slot.start_time,
        end_time: slot.end_time,
        days: typeof slot.days === 'string' ? slot.days.split(',').map(Number) : slot.days,
        slot_group_id: slot.slot_group_id,
        sequence_order: slot.sequence_order || 0,
        days_after_previous: slot.days_after_previous !== undefined ? slot.days_after_previous : 1
      }

      await ChannelViewingSlotApi.update(slotData)
      await this.fetchChannelViewingSlots()
    }
  }
}
</script>

<style scoped>
.groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.group-card {
  text-align: left;
}

.group-header {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.group-slots {
  max-height: 200px;
  overflow-y: auto;
}

.slot-row {
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.slot-row:last-child {
  border-bottom: none;
}

.ungrouped-list {
  text-align: left;
}

.ungrouped-slot {
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  margin-bottom: 8px;
}

.edit-slot-row {
  margin-bottom: 10px;
  padding: 10px;
}
</style>
